import React, { useEffect, useState, useCallback } from 'react';
import { X, Download, Share, Plus } from 'lucide-react';

const DISMISS_KEY = 'eptral_pwa_prompt_dismissed_at';
const INSTALLED_KEY = 'eptral_pwa_installed';
const DISMISS_DAYS = 30;

const isStandalone = () =>
  (typeof window !== 'undefined') &&
  (window.matchMedia?.('(display-mode: standalone)')?.matches || window.navigator?.standalone === true);

const isIOS = () => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const iOSDevice = /iphone|ipad|ipod/i.test(ua);
  // iPadOS 13+ reports as Mac but is touch-capable
  const iPadOS = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  return iOSDevice || iPadOS;
};

const isMobileViewport = () =>
  (typeof window !== 'undefined') &&
  (window.matchMedia?.('(max-width: 900px)')?.matches ||
    (navigator.maxTouchPoints || 0) > 0);

const recentlyDismissed = () => {
  try {
    if (localStorage.getItem(INSTALLED_KEY) === '1') return true;
    const ts = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
    if (!ts) return false;
    return (Date.now() - ts) < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch { return false; }
};

/**
 * Install App prompt for the Participant PWA.
 * - Android/Chromium: uses the captured `beforeinstallprompt` event for a one-tap install.
 * - iOS Safari: shows "Add to Home Screen" guidance (iOS has no install event).
 * - Never nags: hidden when already installed/standalone, and suppressed for 30 days after dismissal.
 */
const InstallAppPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIosSteps, setShowIosSteps] = useState(false);

  const iosMode = isIOS();

  useEffect(() => {
    if (isStandalone() || recentlyDismissed() || !isMobileViewport()) return;

    // Android / Chromium — capture the install event and reveal the banner
    const onBIP = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', onBIP);

    const onInstalled = () => {
      try { localStorage.setItem(INSTALLED_KEY, '1'); } catch {}
      setVisible(false);
      setDeferredPrompt(null);
    };
    window.addEventListener('appinstalled', onInstalled);

    // iOS has no beforeinstallprompt — reveal guidance after a short, non-intrusive delay
    let iosTimer;
    if (iosMode) {
      iosTimer = setTimeout(() => setVisible(true), 1500);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', onBIP);
      window.removeEventListener('appinstalled', onInstalled);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, [iosMode]);

  const dismiss = useCallback(() => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
    setVisible(false);
    setShowIosSteps(false);
  }, []);

  const install = useCallback(async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try {
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        try { localStorage.setItem(INSTALLED_KEY, '1'); } catch {}
      } else {
        try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
      }
    } catch {}
    setDeferredPrompt(null);
    setVisible(false);
  }, [deferredPrompt]);

  if (!visible) return null;
  // On Android we need the captured event; on iOS we show guidance instead.
  if (!iosMode && !deferredPrompt) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[1000] px-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] pt-3 pointer-events-none"
      role="region"
      aria-label="Install Eptral app"
      aria-live="polite"
      data-pwa-install-prompt
    >
      <div className="pointer-events-auto mx-auto w-full max-w-md rounded-2xl bg-white shadow-2xl border border-gray-200 overflow-hidden">
        <div className="flex items-start gap-3 p-4">
          <img src="/icons/icon-192.png" alt="" className="w-12 h-12 rounded-xl flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-gray-900 leading-tight">Install the Eptral app</p>
            <p className="text-xs text-gray-500 mt-0.5">
              {iosMode
                ? 'Add Eptral to your Home Screen for a full-screen, app-like experience.'
                : 'Get a faster, full-screen experience — right from your home screen.'}
            </p>
          </div>
          <button
            onClick={dismiss}
            aria-label="Dismiss"
            className="flex-shrink-0 w-8 h-8 -mt-1 -mr-1 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {iosMode ? (
          <div className="px-4 pb-4">
            {!showIosSteps ? (
              <button
                onClick={() => setShowIosSteps(true)}
                className="w-full bg-[#0056B3] hover:bg-[#004494] text-white text-sm font-medium rounded-xl py-2.5"
              >
                Show me how
              </button>
            ) : (
              <ol className="space-y-2 text-sm text-gray-700 bg-blue-50 rounded-xl p-3">
                <li className="flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-[#0056B3] text-white text-xs flex items-center justify-center flex-shrink-0">1</span>
                  <span className="flex items-center gap-1">Tap the Share icon <Share className="w-4 h-4 inline text-[#0056B3]" /> in the browser bar.</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-[#0056B3] text-white text-xs flex items-center justify-center flex-shrink-0">2</span>
                  <span className="flex items-center gap-1">Choose <strong>Add to Home Screen</strong> <Plus className="w-4 h-4 inline text-[#0056B3]" />.</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-[#0056B3] text-white text-xs flex items-center justify-center flex-shrink-0">3</span>
                  <span>Tap <strong>Add</strong> — then open Eptral from your Home Screen.</span>
                </li>
              </ol>
            )}
          </div>
        ) : (
          <div className="px-4 pb-4">
            <button
              onClick={install}
              className="w-full bg-[#0056B3] hover:bg-[#004494] text-white text-sm font-semibold rounded-xl py-2.5 flex items-center justify-center gap-2"
            >
              <Download className="w-4 h-4" /> Install App
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default InstallAppPrompt;
